import { useMemo } from "react"

import { Truck } from "lucide-react"

import { isInternalStopType } from "@/lib/haulageUtils"
import {
  type CapacityStop,
  calculateVehicleCapacity,
  findVehicleType,
} from "@/lib/routeCostCalculation"
import { cn } from "@/lib/utils"
import type { Location, PlannedRoute, VehicleType } from "@/types/collections"

export function RouteLoadFactorCard({
  route,
  vehicleTypeName,
  vehicleTypes,
  locations,
  getVolumeForecastForStop,
  getVolumeForecast,
}: {
  route: PlannedRoute | null
  vehicleTypeName: string
  vehicleTypes: VehicleType[] | undefined
  locations: Location[] | undefined
  getVolumeForecastForStop: (route: PlannedRoute, stop: { locationId?: string }) => number
  getVolumeForecast: (locationId: string) => number
}) {
  const capacityInfo = useMemo(() => {
    if (!vehicleTypeName || !vehicleTypes) return null

    const vehicleType = findVehicleType(vehicleTypes, vehicleTypeName)
    if (!vehicleType) return null

    const routeStops = route?.stops || []

    const hasShipperCollection = routeStops.some((stop) => stop.slotType === "shipper_collection")

    if (!hasShipperCollection) return null

    const stopsForCapacity: CapacityStop[] = routeStops
      .filter((stop) => !isInternalStopType(stop.slotType))
      .map((stop) => {
        // Per-stop forecast from the instance, not the location sum.
        const volumeForecast =
          route && stop.locationId
            ? getVolumeForecastForStop(route, stop)
            : stop.locationId
              ? getVolumeForecast(stop.locationId)
              : 0

        return {
          volumeEstimated: volumeForecast || (stop.volumeEstimated ?? 0),
          loadingUnitsEstimated: stop.loadingUnitsEstimated ?? 0,
          locationId: stop.locationId,
          slotType: stop.slotType,
        }
      })

    return calculateVehicleCapacity(vehicleType, stopsForCapacity, locations)
  }, [route, vehicleTypeName, vehicleTypes, locations, getVolumeForecastForStop, getVolumeForecast])

  if (!capacityInfo) return null

  const contributions = Object.entries(capacityInfo.usedCapacity)
    .filter(([_, used]) => used > 0)
    .map(([unitType, used]) => {
      const unitTypeKey = unitType as keyof typeof capacityInfo.vehicleCapacity
      const capacity = capacityInfo.vehicleCapacity[unitTypeKey]
      const fillContribution = capacity > 0 ? (used / capacity) * 100 : 0

      const unitTypeName = unitType.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())

      return { unitTypeName, used, fillContribution }
    })

  return (
    <div className="mt-4 pt-4 border-t">
      <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-4 border border-purple-200/50 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-purple-100 rounded-lg">
              <Truck size={16} className="text-purple-700" />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-palette-jungleGreen900">
                Load Factor
              </h4>
              <p className="text-xs text-palette-jungleGreen500">Fill percentage</p>
            </div>
          </div>
          <div className="text-right">
            <div
              className={cn(
                "text-2xl font-bold",
                capacityInfo.totalFillPercentage >= 100
                  ? "text-red-600"
                  : capacityInfo.totalFillPercentage >= 80
                    ? "text-amber-600"
                    : "text-green-600",
              )}
            >
              {capacityInfo.totalFillPercentage.toFixed(0)}%
            </div>
            <div className="text-xs text-palette-jungleGreen500 mt-0.5">
              Vehicle Fill
            </div>
          </div>
        </div>

        <div className="w-full bg-palette-jungleGreen200 rounded-full h-3 mb-3">
          <div
            className={cn(
              "h-3 rounded-full transition-all",
              capacityInfo.totalFillPercentage >= 100
                ? "bg-red-500"
                : capacityInfo.totalFillPercentage >= 80
                  ? "bg-amber-500"
                  : "bg-green-500",
            )}
            style={{ width: `${Math.min(capacityInfo.totalFillPercentage, 100)}%` }}
          />
        </div>

        {contributions.length === 0 ? (
          <div className="text-xs text-palette-jungleGreen500 text-center py-1">
            No loading units planned
          </div>
        ) : (
          <div className="space-y-1">
            <div className="text-xs font-medium text-palette-jungleGreen600 mb-1">
              What's on board:
            </div>
            {contributions.map(({ unitTypeName, used, fillContribution }) => (
              <div key={unitTypeName} className="flex items-center justify-between text-xs">
                <span className="text-palette-jungleGreen700">
                  {used} {unitTypeName}
                </span>
                <span
                  className={cn(
                    "font-medium",
                    fillContribution >= 50
                      ? "text-amber-600"
                      : fillContribution >= 25
                        ? "text-blue-600"
                        : "text-palette-jungleGreen600",
                  )}
                >
                  {fillContribution.toFixed(0)}%
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
